import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2, Sun, Moon, Coffee, Utensils } from "lucide-react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import LoadingScreen from "../Utils/LoadingScreen";
import CreateSlotModal from "./CreateSlotModal";
import * as Dialog from "@radix-ui/react-dialog";

export default function SlotManager() {
  const { baseurl, token, accessToken, setIsUnauthorised,AdminId } = useGlobalContext();
  const [slotsData, setSlotsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDate, setSelectedDate] = useState();
  const itemsPerPage = 5;

  // Category + Status Configs
  const categoryConfig = {
    lunch: {
      color: "bg-yellow-100 text-yellow-800 border-yellow-300",
      icon: <Utensils size={14} />,
    },
    evening: {
      color: "bg-blue-100 text-blue-800 border-blue-300",
      icon: <Coffee size={14} />,
    },
    morning: {
      color: "bg-orange-100 text-orange-800 border-orange-300",
      icon: <Sun size={14} />,
    },
    night: {
      color: "bg-gray-200 text-gray-800 border-gray-300",
      icon: <Moon size={14} />,
    },
  };

  const statusConfig = {
    open: "bg-green-100 text-green-700 border-green-300",
    closed: "bg-red-100 text-red-700 border-red-300",
    pending: "bg-yellow-100 text-yellow-700 border-yellow-300",
  };

  // Fetch Slots API
  const fetchSlots = async (date = selectedDate) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${baseurl}/api/Driver/v2.0/getSlotsList`,
        { date,adminId:AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (response.data.responseCode === "00") {
        const [year, month, day] = date.split("-");
        const months = [
          "JAN",
          "FEB",
          "MAR",
          "APR",
          "MAY",
          "JUN",
          "JUL",
          "AUG",
          "SEP",
          "OCT",
          "NOV",
          "DEC",
        ];
        const jsDate = new Date(year, month - 1, day);
        const dayName = jsDate
          .toLocaleDateString("en-US", { weekday: "long" })
          .toUpperCase();

        const formattedDate = `${day} ${months[month - 1]}, ${dayName}`;
        const filtered = response.data.data.filter(
          (slot) => slot.slot_date.toUpperCase() === formattedDate
        );

        setSlotsData(filtered);
        setFilteredData(filtered);
      } else if (response.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(response.data.responseMessage);
        setSlotsData([]);
      }
    } catch {
      toast.error("Error fetching slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots(selectedDate);
  }, [selectedDate]);

  // Delete Slot
  const handleDeleteSlot = async (slotId) => {
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/deleteSlot`,
        { slotId,adminId:AdminId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (res.data.responseCode === "00") {
        toast.success("Slot deleted successfully");
        fetchSlots();
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to delete slot");
      }
    } catch {
      toast.error("Error deleting slot");
    }
  };

  // Filter search/category
  useEffect(() => {
    let filtered = [...slotsData];

    if (searchQuery) {
      filtered = filtered
        .map((group) => ({
          ...group,
          categories: group.categories
            .map((cat) => ({
              ...cat,
              slots: cat.slots.filter(
                (slot) =>
                  slot.slot_title
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  slot.slot_description
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase())
              ),
            }))
            .filter((cat) => cat.slots.length > 0),
        }))
        .filter((group) => group.categories.length > 0);
    }

    if (categoryFilter) {
      filtered = filtered
        .map((group) => ({
          ...group,
          categories: group.categories.filter(
            (cat) => cat.category === categoryFilter
          ),
        }))
        .filter((group) => group.categories.length > 0);
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, slotsData]);

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const [driverList, setDriverList] = useState([]);
  const [driverListLoading, setDriverListLoading] = useState(false);

  const fetchDriverList = async (slotId) => {
    setDriverListLoading(true);
    try {
      const res = await axios.post(
        `${baseurl}/api/Driver/v1.0/getSlotBookedDriverList`,
        { slotId,adminId:AdminId },
        { headers: { Authorization: `Basic ${token}` } }
      );

      if (res.data.responseCode === "00") {
        setDriverList(res.data.data || []);
      } else if (res.data.responseCode === "401") {
        setIsUnauthorised(true);
      } else {
        toast.error(res.data.responseMessage || "Failed to fetch drivers");
        setDriverList([]);
      }
    } catch {
      toast.error("Error fetching driver list");
    } finally {
      setDriverListLoading(false);
    }
  };

  return (
    <div className="p-5 bg-gray-50 min-h-screen">
      <div className="flex flex-wrap items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-800">Slots Manager</h1>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Date Filter */}
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
          />

          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
            placeholder="Search slots..."
            className="px-3 py-1 border border-gray-300 rounded-md text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
          />

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-1 shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-400"
          >
            <option value="">All Categories</option>
            {Array.from(
              new Set(
                slotsData.flatMap((group) =>
                  group.categories.map((cat) => cat.category)
                )
              )
            ).map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          <button
            onClick={() => setSlotModalOpen(true)}
            className="flex items-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-1 rounded-md text-sm font-medium shadow"
          >
            <Plus size={16} />
            <span>Create Slot</span>
          </button>
        </div>
      </div>

      {/* Slot Cards */}
      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          {paginatedData.map((slotGroup) => (
            <div
              key={slotGroup.id}
              className="mb-6 border border-gray-200 bg-gray-100 rounded-lg shadow-sm"
            >
              <div
                className="px-4 py-3 border-b border-gray-200 flex justify-between items-center rounded"
                style={{ backgroundColor: "#FC981B", color: "white" }}
              >
                <h2 className="font-semibold text-lg text-white">
                  {slotGroup.slot_date}
                </h2>
                <span className="text-md text-white-500">
                  {slotGroup.slot_title}
                </span>
              </div>

              <div className="p-4">
                {slotGroup.categories.map((category) => {
                  const cat = category.category.toLowerCase();
                  const catStyle =
                    categoryConfig[cat] || categoryConfig.morning;

                  return (
                    <div key={category.id} className="mb-4">
                      {/* Category Badge with Icon */}
                      <div
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded border ${catStyle.color}`}
                      >
                        {catStyle.icon}
                        <span>{category.category.toUpperCase()}</span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                        {category.slots.map((slot) => (
                          <div
                            key={slot.id}
                            className="relative bg-white rounded-xl shadow-md border-l-4 p-4 mb-4"
                            style={{
                              borderColor: "#FC981B",
                              minHeight: "190px",
                            }}
                          >
                            {/* Header Row */}
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-base font-semibold">
                                Slot: {slot.slot_title} / {slot.slot_date}
                              </span>

                              <div className="flex items-center gap-3">
                                {/* Status Badge */}
                                <span
                                  className={`inline-flex items-center justify-center text-xs font-medium rounded-full ${
                                    statusConfig[slot.booking_status] ||
                                    statusConfig.pending
                                  }`}
                                  style={{
                                    minWidth: "110px",
                                    height: "28px",
                                    padding: "0 0.6rem",
                                    fontSize: "13px",
                                  }}
                                >
                                  {slot.booking_status.charAt(0).toUpperCase() +
                                    slot.booking_status.slice(1)}
                                </span>

                                {/* Delete Button */}
                                <Dialog.Root>
                                  <Dialog.Trigger asChild>
                                    <button className="text-red-500 hover:text-red-700">
                                      <Trash2 size={18} />
                                    </button>
                                  </Dialog.Trigger>

                                  <Dialog.Portal>
                                    <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
                                    <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-xs -translate-x-1/2 -translate-y-1/2 bg-white p-4 rounded shadow z-50 space-y-4">
                                      <Dialog.Title className="text-lg font-semibold">
                                        Confirm Deletion
                                      </Dialog.Title>
                                      <Dialog.Description className="text-sm text-gray-600">
                                        Are you sure you want to delete this
                                        slot?
                                      </Dialog.Description>
                                      <div className="flex justify-end gap-2">
                                        <Dialog.Close asChild>
                                          <button className="px-3 py-1 text-sm border rounded">
                                            Cancel
                                          </button>
                                        </Dialog.Close>
                                        <button
                                          onClick={() =>
                                            handleDeleteSlot(slot.id)
                                          }
                                          className="px-3 py-1 text-sm text-white bg-red-600 hover:bg-red-700 rounded"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </Dialog.Content>
                                  </Dialog.Portal>
                                </Dialog.Root>
                              </div>
                            </div>

                            <hr className="my-2 border-gray-300" />

                            {/* Details Section */}
                            <div className="bg-gray-100 rounded-md p-3 space-y-3 text-sm text-gray-700">
                              <div className="flex justify-between">
                                <b>Time:</b>
                                <span>
                                  {slot.from_time} - {slot.to_time}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <b>Amount Range:</b>
                                <span>
                                  ₹{slot.min_amount} - ₹{slot.max_amount}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <b>Description:</b>
                                <span>{slot.slot_description}</span>
                              </div>
                            </div>

                            {/* Footer Row (Driver Badge) */}
                            <div className="mt-4 flex justify-start">
                              <Dialog.Root>
                                <Dialog.Trigger asChild>
                                  <button
                                    className="bg-blue-100 text-blue-800 border border-blue-300 rounded-full px-3 py-1 text-xs font-semibold hover:bg-blue-200"
                                    onClick={() => fetchDriverList(slot.id)}
                                  >
                                    {slot.driverBookings} Driver
                                    {slot.driverBookings > 1 ? "s" : ""}
                                  </button>
                                </Dialog.Trigger>

                                <Dialog.Portal>
                                  <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
                                  <Dialog.Content className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded shadow-lg z-50 space-y-4">
                                    <Dialog.Title className="text-lg font-semibold">
                                      Booked Drivers
                                    </Dialog.Title>
                                    <Dialog.Description className="text-sm text-gray-600">
                                      Drivers assigned to this slot
                                    </Dialog.Description>

                                    {driverListLoading ? (
                                      <div className="text-center text-gray-500 text-sm">
                                        Loading...
                                      </div>
                                    ) : driverList.length > 0 ? (
                                      <ul className="space-y-2 max-h-60 overflow-y-auto text-sm">
                                        {driverList.map((driver) => (
                                          <li
                                            key={driver.id}
                                            className="border-b pb-2 flex flex-col gap-1 text-gray-700"
                                          >
                                            <div className="flex justify-between">
                                              <span className="font-medium">
                                                {driver.driverName}
                                              </span>
                                              <span className="text-xs text-gray-500">
                                                {driver.driverMobile}
                                              </span>
                                            </div>
                                            <span className="text-xs bg-gray-100 px-2 py-0.5 rounded w-fit border">
                                              Tier: {driver.driverTier}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <div className="text-gray-500 text-sm">
                                        No drivers booked yet.
                                      </div>
                                    )}

                                    <Dialog.Close asChild>
                                      <button className="px-3 py-1 text-sm border rounded mt-3">
                                        Close
                                      </button>
                                    </Dialog.Close>
                                  </Dialog.Content>
                                </Dialog.Portal>
                              </Dialog.Root>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Pagination */}
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border text-sm hover:bg-gray-100 disabled:opacity-50"
            >
              Previous
            </button>

            <span className="text-sm font-medium">Page {currentPage}</span>

            <button
              onClick={() =>
                setCurrentPage((p) =>
                  p * itemsPerPage < filteredData.length ? p + 1 : p
                )
              }
              disabled={currentPage * itemsPerPage >= filteredData.length}
              className="px-3 py-1 rounded border text-sm hover:bg-gray-100 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}

      {/* Create Slot Modal */}
      {slotModalOpen && (
        <CreateSlotModal
          isOpen={slotModalOpen}
          onClose={() => setSlotModalOpen(false)}
          onCreated={fetchSlots}
        />
      )}
    </div>
  );
}
